import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { listMocoSyncLog, type MocoSyncLogRow } from "@/lib/moco.functions";

export const Route = createFileRoute("/_admin/admin/invoices/sync-log")({
  head: () => ({
    meta: [
      { title: "Moco-Sync-Protokoll – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SyncLogPage,
});

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  dateStyle: "medium",
  timeStyle: "short",
});

function statusBadge(s: MocoSyncLogRow["status"]) {
  const map: Record<string, string> = {
    running: "bg-secondary text-foreground",
    success: "bg-brand-wahlen/20 text-foreground",
    partial: "bg-brand-accent/20 text-foreground",
    error: "bg-destructive/15 text-destructive",
  };
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold ${map[s]}`}>
      {s}
    </span>
  );
}

function SyncLogPage() {
  const listFn = useServerFn(listMocoSyncLog);
  const { data, isLoading, error } = useQuery({
    queryKey: ["moco-sync-log"],
    queryFn: () => listFn(),
  });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  return (
    <div>
      <Link
        to="/admin/invoices"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zu Rechnungen
      </Link>
      <h1 className="mt-2 font-display text-3xl font-extrabold md:text-4xl">
        Moco-Sync-Protokoll
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Letzte 100 Sync-Läufe (täglich per Cron und manuell). Nur Administratoren
        sehen dieses Protokoll.
      </p>

      {error && (
        <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-lg border border-border bg-background">
        {isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden …
          </div>
        ) : !data || data.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            Noch keine Sync-Läufe protokolliert.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-6 px-2 py-3"></th>
                <th className="px-4 py-3 font-semibold">Start</th>
                <th className="px-4 py-3 font-semibold">Auslöser</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Neu</th>
                <th className="px-4 py-3 text-right font-semibold">Aktual.</th>
                <th className="px-4 py-3 text-right font-semibold">Versteckt</th>
                <th className="px-4 py-3 text-right font-semibold">Skips</th>
                <th className="px-4 py-3 text-right font-semibold">Fehler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((row) => {
                const open = !!expanded[row.id];
                const skips = Array.isArray(row.skips) ? row.skips : [];
                const errors = Array.isArray(row.errors) ? row.errors : [];
                const statusChanges = Array.isArray(row.client_status_changes)
                  ? row.client_status_changes
                  : [];
                const hasDetails =
                  skips.length > 0 ||
                  errors.length > 0 ||
                  statusChanges.length > 0 ||
                  !!row.error_message;
                return (
                  <>
                    <tr key={row.id} className="align-middle">
                      <td className="px-2 py-3">
                        {hasDetails && (
                          <button
                            aria-label={open ? "Zuklappen" : "Aufklappen"}
                            onClick={() =>
                              setExpanded((s) => ({ ...s, [row.id]: !s[row.id] }))
                            }
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {open ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {dateFmt.format(new Date(row.started_at))}
                      </td>
                      <td className="px-4 py-3">{row.trigger}</td>
                      <td className="px-4 py-3">{statusBadge(row.status)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.invoices_created}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.invoices_updated}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.invoices_hidden}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.invoices_skipped}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.invoices_errors}
                      </td>
                    </tr>
                    {open && hasDetails && (
                      <tr key={`${row.id}-details`} className="bg-secondary/30">
                        <td></td>
                        <td colSpan={8} className="px-4 py-3">
                          {statusChanges.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs font-semibold uppercase text-muted-foreground">
                                Statuswechsel Kunden ({statusChanges.length})
                              </p>
                              <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-foreground">
                                {statusChanges.slice(0, 50).map((c: any, i: number) => (
                                  <li key={i}>
                                    {c.organization_name}{" "}
                                    <span className="font-mono text-muted-foreground">
                                      ({c.moco_client_number})
                                    </span>{" "}
                                    · {c.from} → {c.to} · aktive Projekte: {c.active_projects}/
                                    {c.total_projects}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {row.error_message && (
                            <p className="mb-2 text-sm text-destructive">
                              Fataler Fehler: {row.error_message}
                            </p>
                          )}
                          {errors.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs font-semibold uppercase text-muted-foreground">
                                Fehler ({errors.length})
                              </p>
                              <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-foreground">
                                {errors.slice(0, 50).map((e: any, i: number) => (
                                  <li key={i}>
                                    {e.moco_invoice_id ? (
                                      <span className="font-mono">
                                        [{e.moco_invoice_id}]{" "}
                                      </span>
                                    ) : null}
                                    {e.message}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {skips.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold uppercase text-muted-foreground">
                                Skips ({skips.length})
                              </p>
                              <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-foreground">
                                {skips.slice(0, 50).map((s: any, i: number) => (
                                  <li key={i}>
                                    {s.identifier ? (
                                      <span className="font-mono">
                                        {s.identifier}{" "}
                                      </span>
                                    ) : null}
                                    · Kunde {s.moco_company_id ?? "–"} — {s.reason}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
